import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import {
  getNearbyDeals,
  getRecentDeals,
  getWalkerDistanceMap,
  MAX_NEARBY_RADIUS_M,
} from '../services/apiService';
import { calculateDistance, formatDistanceNL } from '../services/locationService';
import './WelcomePage.css';

// Only used when the walker's location is unknown, where there is nothing to
// rank on — the list is scrollable, so this is a request bound, not a display
// cap.
const HOME_FALLBACK_DEAL_LIMIT = 20;

function readUserLocation() {
  const lat = parseFloat(sessionStorage.getItem('walkin_user_lat'));
  const lng = parseFloat(sessionStorage.getItem('walkin_user_lng'));
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

// A missing coordinate arrives as null, and Number(null) is 0 — which would
// silently place the shop off the coast of Africa — so blanks are rejected
// before coercion.
function coord(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

// Real walker → shop distance, or null to hide the label.
//
// The deal's own coordinates win when it has them, but /deals/nearby does not
// project them today, so in practice this resolves through walkerDistances —
// a lookup measured from the walker's position. The deal's incoming
// distanceM is deliberately NOT used as a fallback: on the gebied-scoped path
// it is measured from the area centroid, and since deals sit on that centroid
// it is always 0. That is what put "0 m" on every row.
function resolveDistanceM(deal, user, walkerDistances) {
  if (!user) return null;
  const lat = coord(deal?.lat);
  const lng = coord(deal?.lng);
  if (lat != null && lng != null) {
    return calculateDistance(user.lat, user.lng, lat, lng);
  }
  const measured = walkerDistances.get(deal?.id);
  return Number.isFinite(measured) ? measured : null;
}

// Nearest first. Deals we could not measure keep their incoming order at the
// end of the list instead of being dropped.
function byDistance(a, b) {
  const av = Number.isFinite(a.distanceM) ? a.distanceM : Infinity;
  const bv = Number.isFinite(b.distanceM) ? b.distanceM : Infinity;
  return av - bv;
}

export default function WelcomePage() {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loadingDeals, setLoadingDeals] = useState(true);
  const listRef = useRef(null);
  // Drives the bottom fade: true only while there is list left to scroll, so
  // a list that fits gets no fade at all and the fade goes once the walker
  // reaches the last deal.
  const [moreBelow, setMoreBelow] = useState(false);

  const syncMoreBelow = useCallback(() => {
    const el = listRef.current;
    if (el) setMoreBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 1);
  }, []);

  // Every relevant active deal from the backend — the list itself scrolls, so
  // there is no cap on what is shown. When the walker's location is already
  // known (they have been through /detect before) the deals are everything
  // within reach, sorted nearest first; otherwise fall back to the most recent
  // active deals so the page is never blank.
  useEffect(() => {
    let cancelled = false;

    async function load() {
      const user = readUserLocation();

      try {
        // Deliberately unscoped: no areaId, so a gebied picked earlier in
        // /detect cannot narrow this list. The home page answers "what is
        // near me", and coming back to it from a deal or the feed used to
        // leave the walker looking at one winkelcentrum's deals. The feed
        // keeps its gebied scoping; walkin_area_id stays where it is.
        const found = user
          ? await getNearbyDeals(user.lat, user.lng, MAX_NEARBY_RADIUS_M)
          : await getRecentDeals(HOME_FALLBACK_DEAL_LIMIT);
        const walkerDistances = user
          ? await getWalkerDistanceMap(user.lat, user.lng)
          : new Map();
        const measured = found
          .map((deal) => ({
            ...deal,
            distanceM: resolveDistanceM(deal, user, walkerDistances),
          }))
          .sort(byDistance);
        if (!cancelled) setDeals(measured);
      } catch {
        if (!cancelled) setDeals([]);
      } finally {
        if (!cancelled) setLoadingDeals(false);
      }
    }

    // Coming back to the page has to re-fetch. Deals expire while the tab sits
    // in the background, and a walker who switched gebied elsewhere — or who
    // returned through the browser's back button, where the whole page comes
    // out of the bfcache with its old state intact — would otherwise be
    // reading a list from whenever they left.
    //
    // Returning to a tab fires visibilitychange and focus together, so the
    // reloads are coalesced; without that every return costs two full round
    // trips. load() itself is untouched.
    let reloading = false;
    const reload = () => {
      if (cancelled || reloading) return;
      reloading = true;
      load().finally(() => { reloading = false; });
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') reload();
    };
    const onPageShow = (e) => {
      if (e.persisted) reload();
    };

    reload();
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', reload);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('focus', reload);
    };
  }, []);

  // Re-check after the list renders and whenever the viewport changes size:
  // the height is capped in dvh, so a rotation can change whether it overflows.
  useEffect(() => {
    syncMoreBelow();
    window.addEventListener('resize', syncMoreBelow);
    return () => window.removeEventListener('resize', syncMoreBelow);
  }, [deals, syncMoreBelow]);

  const hasArea = !!sessionStorage.getItem('walkin_area_id');
  const knowsLocation = !!readUserLocation();
  const dealCountLabel = `${deals.length} ${deals.length === 1 ? 'deal' : 'deals'}`;

  return (
    <div className="welcome">
      <div className="welcome__content">

        <header className="welcome__header">
          <Logo size={24} />
          {/* The compass opens the map of deals around you. Without a known
              gebied yet, it first runs location detection and then lands on
              the map (not the feed, which is what the big CTA does). */}
          <button
            type="button"
            className="welcome__icon-btn"
            onClick={() => navigate(hasArea ? '/map' : '/detect?next=map')}
            aria-label="Bekijk deals op de kaart"
            title="Bekijk deals op de kaart"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
            </svg>
            <span className="welcome__icon-btn-label">Kaart</span>
          </button>
        </header>

        <section className="welcome__hero">
          <h1 className="welcome__title">
            <span className="welcome__title-white">Deals om je heen.</span>
            <br />
            <span className="welcome__title-blue">Direct zichtbaar.</span>
          </h1>

          {/* Key USP — sits directly under the headline on purpose. */}
          <div className="welcome__anon">
            <span className="welcome__anon-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
            </span>
            <span className="welcome__anon-text">
              <strong className="welcome__trust-text">Volledig anoniem — geen account nodig</strong>
              <span className="welcome__anon-sub">Geen e-mail, geen naam, geen gegevens.</span>
            </span>
          </div>

          <p className="welcome__subtitle">
            Ontdek de beste aanbiedingen van winkels terwijl je voorbijloopt.
          </p>
        </section>

        <ol className="welcome__steps" aria-label="Zo werkt het">
          <li><span className="welcome__step-n">1</span>Loop rond</li>
          <li><span className="welcome__step-n">2</span>Kies een deal</li>
          <li><span className="welcome__step-n">3</span>Toon je code</li>
        </ol>

        <section className="welcome__nearby">
          <div className="welcome__nearby-head">
            <div className="welcome__nearby-label">In de buurt</div>
            {!loadingDeals && deals.length > 0 && (
              <span className="welcome__nearby-count">
                {knowsLocation ? dealCountLabel : `${dealCountLabel} · recent`}
              </span>
            )}
          </div>

          {loadingDeals ? (
            <div className="welcome__skeleton" aria-label="Deals laden…">
              <span /><span /><span />
            </div>
          ) : deals.length === 0 ? (
            <div className="welcome__nearby-empty">
              Nog geen deals in de buurt. Kom later terug.
            </div>
          ) : (
            <ul
              className={`welcome__deals${moreBelow ? ' welcome__deals--more' : ''}`}
              ref={listRef}
              onScroll={syncMoreBelow}
            >
              {deals.map((deal) => {
                const distance = formatDistanceNL(deal.distanceM);
                return (
                  <li key={deal.id} className="welcome__deal">
                    <button
                      type="button"
                      className="welcome__deal-row"
                      onClick={() => navigate(`/deal/${deal.id}`)}
                    >
                      <span className="welcome__deal-main">
                        <span className="welcome__deal-category">
                          {deal.category || deal.shopName || 'Deal'}
                        </span>
                        <span className="welcome__deal-title">{deal.title}</span>
                        {deal.shopName && deal.category && (
                          <span className="welcome__deal-shop">{deal.shopName}</span>
                        )}
                      </span>
                      {distance && (
                        <span className="welcome__deal-distance">{distance}</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <div className="welcome__spacer" />

        <div className="welcome__bottom">
          <button
            className="welcome__cta"
            onClick={() => navigate('/detect')}
          >
            {knowsLocation ? 'Bekijk alle deals in de buurt' : 'Ontdek deals in de buurt'}
          </button>
          <div className="welcome__footer-links">
            <button
              type="button"
              className="welcome__shop-link"
              onClick={() => navigate('/shop/login')}
            >
              Bent u een winkelier? Klik hier
            </button>
            <span className="welcome__footer-sep" aria-hidden="true">·</span>
            <button
              type="button"
              className="welcome__admin-link"
              onClick={() => navigate('/admin/login')}
            >
              Admin
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
